import React, { useEffect, useRef, useState } from "react";
import "../Auth.css";
import "./ForgetPassword.css";
import { AiOutlineUser } from "react-icons/ai";
import { HiLockClosed } from "react-icons/hi";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, CheckCircle2, MailCheck } from "lucide-react";
import { ApiPaths } from "./../../Config/ApiPath";
import { toastFailed, toastSuccess } from "./../../Config/BasicInfo";
import useAxiosHelper from "../../Common/AxiosHelper";
import AuthLayout from "../../Components/AuthLayout";

const OTP_ACTION = "forgot_password";
const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;

const passwordRules = [
  { key: "len", label: "8+ characters", test: (p) => p.length >= 8 },
  { key: "upper", label: "Uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "Lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "num", label: "Number", test: (p) => /\d/.test(p) },
];
// Must match the backend rule (OTPService PASSWORD_RULE)
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,}$/;

const steps = ["Account", "Verify", "New password"];

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const ForgetPassword = () => {
  const navigate = useNavigate();
  const { AxiosPost } = useAxiosHelper();

  const [step, setStep] = useState(0); // 0 identify · 1 code · 2 password · 3 done
  const [identifier, setIdentifier] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [resetUsername, setResetUsername] = useState("");
  const otpRefs = useRef([]);

  const otp = digits.join("");

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (step === 1) otpRefs.current[0]?.focus();
  }, [step]);

  /* ---------- Step 1: send code ---------- */
  const sendCode = async () => {
    const value = identifier.trim();
    setError("");
    if (!value) {
      setError("Enter your username or email");
      return;
    }
    setBusy(true);
    try {
      const res = await AxiosPost(ApiPaths.sendOTP, {
        username: value,
        action: OTP_ACTION,
      });
      setSentTo(res?.sentTo || "");
      setDigits(Array(OTP_LENGTH).fill(""));
      setCooldown(RESEND_SECONDS);
      if (step === 0) setStep(1);
      else toastSuccess("A new code has been sent");
    } catch (e) {
      setError(errorMessage(e, "Could not send the code. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  /* ---------- Step 2: code boxes ---------- */
  const setDigitAt = (index, value) => {
    setError("");
    const clean = value.replace(/\D/g, "");
    if (!clean) {
      setDigits((d) => d.map((x, i) => (i === index ? "" : x)));
      return;
    }
    // Paste / SMS autofill can deliver several digits at once
    setDigits((d) => {
      const next = [...d];
      clean
        .slice(0, OTP_LENGTH - index)
        .split("")
        .forEach((ch, k) => {
          next[index + k] = ch;
        });
      return next;
    });
    const focusAt = Math.min(index + clean.length, OTP_LENGTH - 1);
    otpRefs.current[focusAt]?.focus();
  };

  const onDigitKey = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowLeft" && index > 0) {
      otpRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    } else if (e.key === "Enter") {
      continueFromCode();
    }
  };

  const continueFromCode = () => {
    if (otp.length !== OTP_LENGTH) {
      setError(`Enter the ${OTP_LENGTH}-digit code from your email`);
      return;
    }
    setError("");
    setStep(2);
  };

  /* ---------- Step 3: reset ---------- */
  const resetPassword = async () => {
    setError("");
    if (!PASSWORD_REGEX.test(password)) {
      setError("Password doesn't meet all the requirements below");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      const res = await AxiosPost(ApiPaths.forgotPassword, {
        username: identifier.trim(),
        otp,
        action: OTP_ACTION,
        newPassword: password,
      });
      setResetUsername(res?.username || "");
      setStep(3);
    } catch (e) {
      const message = errorMessage(e, "Could not reset the password");
      // A bad / expired / used code can only be fixed on the code step
      if (/otp/i.test(message)) {
        setDigits(Array(OTP_LENGTH).fill(""));
        setStep(1);
        setError(
          /expired|not found|already used/i.test(message)
            ? "That code has expired. Tap “Resend code” to get a new one."
            : "That code is incorrect. Check your email and try again."
        );
      } else {
        setError(message);
        toastFailed(message);
      }
    } finally {
      setBusy(false);
    }
  };

  const onEnter = (fn) => (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      fn();
    }
  };

  const titles = [
    ["Reset your password", "Enter your username or email and we'll send you a verification code."],
    ["Check your email", sentTo ? `We sent a 6-digit code to ${sentTo}.` : "We sent a 6-digit code to your registered email."],
    ["Create a new password", "Choose a strong password you haven't used before."],
    ["Password updated", "You can now sign in with your new password."],
  ];

  return (
    <AuthLayout
      title={titles[step][0]}
      subtitle={titles[step][1]}
      footerText="Remember your password? "
      footerLinkText="Sign In"
      footerTo="/login"
    >
      {step < 3 ? (
        <ol className="fpSteps" aria-label="Progress">
          {steps.map((label, i) => (
            <li
              key={label}
              className={`fpStep ${i < step ? "isDone" : ""} ${i === step ? "isCurrent" : ""}`}
              aria-current={i === step ? "step" : undefined}
            >
              <span className="fpStepDot">{i < step ? <Check size={13} /> : i + 1}</span>
              <span className="fpStepLabel">{label}</span>
            </li>
          ))}
        </ol>
      ) : null}

      <form className="authForm fpForm" onSubmit={(e) => e.preventDefault()} key={step}>
        {/* ---------- STEP 1 ---------- */}
        {step === 0 && (
          <>
            <div className="authField">
              <label className="fieldLabel" htmlFor="fpIdentifier">
                Username or Email
              </label>
              <div className="fieldBox">
                <i className="fieldIcon">
                  <AiOutlineUser />
                </i>
                <input
                  id="fpIdentifier"
                  type="text"
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="username"
                  placeholder="Enter your username or email"
                  value={identifier}
                  onChange={(e) => {
                    setError("");
                    setIdentifier(e.target.value);
                  }}
                  onKeyDown={onEnter(sendCode)}
                  autoFocus
                />
              </div>
            </div>
            {error ? <p className="authError">{error}</p> : null}

            <button type="button" className="authBtn" onClick={sendCode} disabled={busy}>
              {busy ? <span className="fpSpinner" /> : "Send verification code"}
            </button>
          </>
        )}

        {/* ---------- STEP 2 ---------- */}
        {step === 1 && (
          <>
            <div className="fpMailBadge">
              <MailCheck size={22} />
              <span>
                Code is valid for <b>10 minutes</b>. Check your spam folder if it
                hasn't arrived.
              </span>
            </div>

            <div className="fpOtp" role="group" aria-label="Verification code">
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (otpRefs.current[i] = el)}
                  className={`fpOtpBox ${d ? "isFilled" : ""} ${error ? "isError" : ""}`}
                  type="text"
                  inputMode="numeric"
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  maxLength={OTP_LENGTH}
                  value={d}
                  aria-label={`Digit ${i + 1}`}
                  onChange={(e) => setDigitAt(i, e.target.value)}
                  onKeyDown={(e) => onDigitKey(i, e)}
                  onFocus={(e) => e.target.select()}
                />
              ))}
            </div>
            {error ? <p className="authError fpCenter">{error}</p> : null}

            <button type="button" className="authBtn" onClick={continueFromCode}>
              Continue
            </button>

            <div className="fpRow">
              <button type="button" className="fpLink" onClick={() => { setError(""); setStep(0); }}>
                <ArrowLeft size={15} /> Change account
              </button>
              <button
                type="button"
                className="fpLink"
                onClick={sendCode}
                disabled={busy || cooldown > 0}
              >
                {busy ? "Sending…" : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
              </button>
            </div>
          </>
        )}

        {/* ---------- STEP 3 ---------- */}
        {step === 2 && (
          <>
            <div className="authField">
              <label className="fieldLabel" htmlFor="fpPassword">
                New Password
              </label>
              <div className="fieldBox">
                <i className="fieldIcon">
                  <HiLockClosed />
                </i>
                <input
                  id="fpPassword"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Create a new password"
                  value={password}
                  onChange={(e) => {
                    setError("");
                    setPassword(e.target.value);
                  }}
                  autoFocus
                />
                <i
                  className="fieldEye"
                  onClick={() => setShowPassword((v) => !v)}
                  role="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaRegEye /> : <FaRegEyeSlash />}
                </i>
              </div>
            </div>

            <ul className="fpRules">
              {passwordRules.map((r) => {
                const ok = r.test(password);
                return (
                  <li key={r.key} className={ok ? "isOk" : ""}>
                    <span className="fpRuleDot">{ok ? <Check size={11} /> : null}</span>
                    {r.label}
                  </li>
                );
              })}
            </ul>

            <div className="authField">
              <label className="fieldLabel" htmlFor="fpConfirm">
                Confirm Password
              </label>
              <div className="fieldBox">
                <i className="fieldIcon">
                  <HiLockClosed />
                </i>
                <input
                  id="fpConfirm"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Re-enter your new password"
                  value={confirm}
                  onChange={(e) => {
                    setError("");
                    setConfirm(e.target.value);
                  }}
                  onKeyDown={onEnter(resetPassword)}
                />
              </div>
            </div>
            {confirm && password !== confirm ? (
              <p className="authHint bad">Passwords do not match</p>
            ) : null}
            {error ? <p className="authError">{error}</p> : null}

            <button type="button" className="authBtn" onClick={resetPassword} disabled={busy}>
              {busy ? <span className="fpSpinner" /> : "Reset password"}
            </button>

            <div className="fpRow">
              <button type="button" className="fpLink" onClick={() => { setError(""); setStep(1); }}>
                <ArrowLeft size={15} /> Back to code
              </button>
            </div>
          </>
        )}

        {/* ---------- DONE ---------- */}
        {step === 3 && (
          <div className="fpDone">
            <span className="fpDoneIcon">
              <CheckCircle2 size={40} />
            </span>
            {resetUsername ? (
              <p className="fpDoneUser">
                Account <b>{resetUsername}</b>
              </p>
            ) : null}
            <button
              type="button"
              className="authBtn"
              onClick={() => navigate("/login", { state: { username: resetUsername || identifier.trim() } })}
            >
              Continue to Sign In
            </button>
          </div>
        )}
      </form>
    </AuthLayout>
  );
};

export default ForgetPassword;
